import React, { useEffect, useMemo, useState } from "react";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import { getPanchangIntakeQueue } from "../../services/api/http";
import { CONSULTATION_SECTIONS, DOCUMENT_OPTIONS } from "./panchangConsultationData";
import "./PanchangConsultationPage.css";

const STORAGE_KEY = "panchang-admin-credentials";

const getStoredCredentials = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { username: "", password: "" };
  } catch {
    return { username: "", password: "" };
  }
};

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "None";
  }

  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([key, entry]) => `${key}: ${entry}`)
      .join(" | ");
  }

  return value || "Not provided";
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return "Not provided";
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return timestamp;
  }

  return parsed.toLocaleString();
};

function SelectionGroup({ title, values }) {
  return (
    <div className="panchang-admin-group">
      <div className="panchang-admin-group-title">{title}</div>
      <div className="panchang-admin-pill-list">
        {values.length > 0 ? (
          values.map((value) => (
            <span key={value} className="panchang-admin-pill">
              {value}
            </span>
          ))
        ) : (
          <span className="panchang-admin-pill">None</span>
        )}
      </div>
    </div>
  );
}

export default function PanchangConsultationAdminPage() {
  const [credentials, setCredentials] = useState(() => getStoredCredentials());
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isAuthed, setIsAuthed] = useState(() => {
    const stored = getStoredCredentials();
    return Boolean(stored.username && stored.password);
  });

  const queueStats = useMemo(() => {
    return {
      count: queue.length,
      newest: queue[0]?.createdAt || "None",
      oldest: queue[queue.length - 1]?.createdAt || "None",
    };
  }, [queue]);

  const loadQueue = async (auth = credentials) => {
    if (!auth.username || !auth.password) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await getPanchangIntakeQueue(auth.username, auth.password);
      setQueue(response.data || []);
      setIsAuthed(true);
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
    } catch {
      setQueue([]);
      setIsAuthed(false);
      setError("Unable to load the queue. Check the login credentials.");
      window.sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (credentials.username && credentials.password) {
      loadQueue(credentials);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCredentialsChange = (event) => {
    const { name, value } = event.target;
    setLoginError("");
    setCredentials((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!credentials.username || !credentials.password) {
      setLoginError("Enter the admin username and password.");
      return;
    }

    await loadQueue(credentials);
  };

  const handleLogout = () => {
    window.sessionStorage.removeItem(STORAGE_KEY);
    setQueue([]);
    setCredentials({ username: "", password: "" });
    setIsAuthed(false);
    setError("");
    setLoginError("");
  };

  return (
    <SiteLayout>
      <main className="page-shell panchang-page-shell home-v3-shell">
        <PageIntro
          eyebrow="Admin / Panchang"
          title="Panchang intake queue."
          description="Review protected submissions in arrival order, with the details needed to respond clearly."
          visual={(
            <div className="home-v3-panel panchang-intake-hero-panel">
              <span className="home-v3-kicker">Status</span>
              <div className="panchang-admin-stats">
                <div className="panchang-admin-stat">
                  <span className="panchang-summary-label">Requests</span>
                  <strong>{queueStats.count}</strong>
                </div>
                <div className="panchang-admin-stat">
                  <span className="panchang-summary-label">Newest</span>
                  <strong>{formatTimestamp(queueStats.newest)}</strong>
                </div>
                <div className="panchang-admin-stat">
                  <span className="panchang-summary-label">Oldest</span>
                  <strong>{formatTimestamp(queueStats.oldest)}</strong>
                </div>
              </div>
            </div>
          )}
        />

        <section className="section">
          <div className="home-v3-panel panchang-admin-login">
            <span className="home-v3-kicker">Login</span>
            <h2>Open the queue</h2>
            <p>
              Enter the site admin credentials to load the intake list.
            </p>
            <form className="panchang-admin-form" onSubmit={handleLogin}>
              <label className="form-field">
                <span>Username</span>
                <input
                  type="text"
                  name="username"
                  value={credentials.username}
                  onChange={handleCredentialsChange}
                  className="form-control modern-input"
                  placeholder="Username"
                  autoComplete="username"
                />
              </label>
              <label className="form-field">
                <span>Password</span>
                <input
                  type="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleCredentialsChange}
                  className="form-control modern-input"
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </label>
              {loginError ? <p className="birth-chart-error">{loginError}</p> : null}
              <div className="panchang-actions">
                <button type="submit" className="button-primary" disabled={loading}>
                  {loading ? "Loading..." : "Open Queue"}
                </button>
                {isAuthed ? (
                  <button type="button" className="button-secondary" onClick={handleLogout}>
                    Log Out
                  </button>
                ) : null}
                <button
                  type="button"
                  className="button-secondary"
                  onClick={() => loadQueue(credentials)}
                  disabled={!credentials.username || !credentials.password || loading}
                >
                  Refresh
                </button>
              </div>
              {error ? <p className="birth-chart-error">{error}</p> : null}
            </form>
          </div>

          <div className="home-v3-panel panchang-admin-queue" style={{ marginTop: "18px" }}>
            <span className="home-v3-kicker">Reference</span>
            <h2>Queue structure</h2>
            <p>
              Each submission includes personal information, selected request
              groups, optional partner data, documents, and consultation
              preferences.
            </p>
            <div className="panchang-admin-stats" style={{ marginTop: "18px" }}>
              <div className="panchang-admin-stat">
                <span className="panchang-summary-label">Sections</span>
                <strong>{CONSULTATION_SECTIONS.length}</strong>
              </div>
              <div className="panchang-admin-stat">
                <span className="panchang-summary-label">Documents</span>
                <strong>{DOCUMENT_OPTIONS.length}</strong>
              </div>
              <div className="panchang-admin-stat">
                <span className="panchang-summary-label">Protected</span>
                <strong>Yes</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="home-v3-panel">
            <span className="home-v3-kicker">Queue</span>
            <h2>Latest submissions</h2>
            <p>
              Open each request to review the full intake details.
            </p>

            {queue.length === 0 ? (
              <div className="panchang-admin-entry">
                <div className="panchang-admin-entry-title">
                  <strong>No submissions loaded yet.</strong>
                  <span className="panchang-admin-entry-meta">
                    Log in to load the queue.
                  </span>
                </div>
              </div>
            ) : null}

            {queue.map((submission) => {
              const payload = submission.payload || {};
              const personalInformation = payload.personalInformation || {};
              const partnerDetails = payload.partnerDetails || {};
              const checkedItems = payload.checkedItems || {};

              return (
                <details key={submission.id} className="panchang-admin-entry">
                  <summary>
                    <div className="panchang-admin-entry-title">
                      <strong>{personalInformation.fullName || "Unnamed request"}</strong>
                      <span className="panchang-admin-entry-meta">
                        ID {submission.id} | {submission.status} | {formatTimestamp(
                          submission.createdAt
                        )}
                      </span>
                    </div>
                    <span className="panchang-count">
                      {Object.values(checkedItems).reduce(
                        (total, values) => total + (values?.length || 0),
                        0
                      )}{" "}
                      checked
                    </span>
                  </summary>

                  <div className="panchang-admin-entry-body">
                    <dl className="panchang-admin-dl">
                      <div>
                        <dt>Gender</dt>
                        <dd>{formatValue(personalInformation.gender)}</dd>
                      </div>
                      <div>
                        <dt>Date of Birth</dt>
                        <dd>{formatValue(personalInformation.dateOfBirth)}</dd>
                      </div>
                      <div>
                        <dt>Exact Time of Birth</dt>
                        <dd>{formatValue(personalInformation.exactTimeOfBirth)}</dd>
                      </div>
                      <div>
                        <dt>Place of Birth</dt>
                        <dd>{formatValue(personalInformation.placeOfBirth)}</dd>
                      </div>
                      <div>
                        <dt>Current Location</dt>
                        <dd>{formatValue(personalInformation.currentLocation)}</dd>
                      </div>
                      <div>
                        <dt>Phone / WhatsApp</dt>
                        <dd>{formatValue(personalInformation.phoneOrWhatsApp)}</dd>
                      </div>
                      <div>
                        <dt>Email</dt>
                        <dd>{formatValue(personalInformation.email)}</dd>
                      </div>
                      <div>
                        <dt>Preferred Type</dt>
                        <dd>{formatValue(payload.preferredConsultationType)}</dd>
                      </div>
                    </dl>

                    <SelectionGroup
                      title="Requested checks"
                      values={Object.entries(checkedItems).flatMap(([groupKey, values]) =>
                        values.map((value) => {
                          const groupTitle =
                            CONSULTATION_SECTIONS.find((section) => section.key === groupKey)?.title ||
                            groupKey;
                          return `${groupTitle}: ${value}`;
                        })
                      )}
                    />

                    <SelectionGroup
                      title="Documents to provide"
                      values={payload.documentsToProvide || []}
                    />

                    <div className="panchang-admin-group">
                      <div className="panchang-admin-group-title">Partner details</div>
                      <dl className="panchang-admin-dl">
                        <div>
                          <dt>Partner Name</dt>
                          <dd>{formatValue(partnerDetails.partnerName)}</dd>
                        </div>
                        <div>
                          <dt>DOB</dt>
                          <dd>{formatValue(partnerDetails.partnerDob)}</dd>
                        </div>
                        <div>
                          <dt>Time of Birth</dt>
                          <dd>{formatValue(partnerDetails.partnerTimeOfBirth)}</dd>
                        </div>
                        <div>
                          <dt>Place of Birth</dt>
                          <dd>{formatValue(partnerDetails.partnerPlaceOfBirth)}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="panchang-admin-group">
                      <div className="panchang-admin-group-title">Other temple / ritual service</div>
                      <div>{formatValue(payload.otherTempleService)}</div>
                    </div>

                    <div className="panchang-admin-group">
                      <div className="panchang-admin-group-title">Additional questions or concerns</div>
                      <div>{formatValue(payload.additionalQuestionsOrConcerns)}</div>
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}
